import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useUIStore, useDrawingStore, useCategoriesStore, useGlobalStore } from '../../stores';
import { useUnifiedElementsStore } from '../../stores/unifiedElementsStore';
import { MapService } from '../../services/map/mapService';
import { createDrawingService, DrawingMode } from '../../services/drawing/drawingService';
import { logger } from '../../../../shared/utils/logger';
import './DrawingPanel.css';

interface DrawingPanelProps {
  isOpen: boolean;
  onClose: () => void;
  mapService: MapService | null;
}

const DrawingPanel: React.FC<DrawingPanelProps> = ({ isOpen, onClose, mapService }) => {
  const { addNotification } = useUIStore();
  const {
    currentMode,
    isDrawing,
    selectedFeatures,
    setMode,
    setDrawing
  } = useDrawingStore();
  const { getAllCategories, initializeCategories } = useCategoriesStore();
  const { selectedStoreyId, setSelectedStoreyId } = useGlobalStore();

  const [isLayerVisible, setIsLayerVisible] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('VPP');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Obtener categorías del store
  const categories = getAllCategories();

  // Inicializar categorías si no están cargadas
  useEffect(() => {
    if (categories.length === 0) {
      initializeCategories();
    }
    
    // Establecer referencia global al store de categorías para que el MapService pueda acceder
    (window as any).__categoriesStore = useCategoriesStore.getState();
    
    // Refrescar estilos después de inicializar categorías
    setTimeout(async () => {
      if (mapService && typeof mapService.refreshDrawLayerStyles === 'function') {
        await mapService.refreshDrawLayerStyles();
        logger.log('DRAWING_PANEL', '🎨 DrawingPanel: Estilos refrescados después de inicializar categorías');
      }
    }, 100);
  }, [categories.length, initializeCategories, mapService]);

  const drawingService = useMemo(() => {
    if (!mapService) return null;
    try {
      logger.log('DRAWING_PANEL', '🔧 DrawingPanel: Creando DrawingService...');
      const service = createDrawingService(mapService, {
        enableSnap: true,
        enableRightClickCancel: true,
        defaultCategory: selectedCategory
      });
      logger.log('DRAWING_PANEL', '✅ DrawingPanel: DrawingService creado exitosamente');
      return service;
    } catch (e) {
      logger.error('DRAWING_PANEL', 'No se pudo crear DrawingService:', e);
      return null;
    }
  }, [mapService, selectedCategory]);

  // Efecto para manejar la apertura/cierre del panel (después de crear drawingService)
  useEffect(() => {
    if (isOpen) {
      // Al abrir el panel, preparar el modo de dibujo; lo iniciaremos tras init
      logger.log('DRAWING_PANEL', '🎨 DrawingPanel: Panel abierto, preparando modo de dibujo');
      setMode(DrawingMode.DRAW);
      setDrawing(true);
    } else {
      // Al cerrar el panel, desactivar el modo de dibujo
      logger.log('DRAWING_PANEL', '🎨 DrawingPanel: Panel cerrado, desactivando modo de dibujo');
      if (drawingService) {
        drawingService.stopCurrentMode();
      }
      setDrawing(false);
      // Resetear el modo a dibujo para la próxima apertura
      setMode(DrawingMode.DRAW);
    }
  }, [isOpen, drawingService, setMode, setDrawing]);

    useEffect(() => {
    let mounted = true;
    (async () => {
      if (drawingService) {
        try {
          logger.log('DRAWING_PANEL', '🔧 DrawingPanel: Inicializando DrawingService...');
          await drawingService.init();
          logger.log('DRAWING_PANEL', '✅ DrawingPanel: DrawingService inicializado exitosamente');
          if (mounted) {
             // Actualizar el estado de visibilidad de la capa activa
             const activeLayer = mapService?.getActiveDrawLayer();
             setIsLayerVisible(activeLayer?.getVisible() ?? true);

                         // Obtener la planta activa del mapa (la que está visible)
             const gui = mapService?.getGUI();
             if (gui) {
                            const options = gui.getStoreyDropdownOptions();
             logger.log('DRAWING_PANEL', '🏗️ DrawingPanel: Opciones de plantas disponibles:', options);
             
             // Obtener la planta actualmente seleccionada en el mapa
             const activeStoreyId = gui.getSelectedStoreyId();
             logger.log('DRAWING_PANEL', '🏗️ DrawingPanel: Planta activa en el mapa:', activeStoreyId);
               
               if (options.length > 0) {
                 // Si hay una planta activa, usarla; si no, usar la primera
                 const storeyToSelect = activeStoreyId || options[0].id;
                 setSelectedStoreyId(storeyToSelect);
                 
                 // Solo cambiar la planta si no hay ninguna seleccionada actualmente
                 if (!activeStoreyId) {
                   gui.selectStoreyById(storeyToSelect);
                 }
               }
             }

            drawingService.onModeChange((mode) => {
              setMode(mode);
            });

            drawingService.onFeatureCreated(() => {
              addNotification({
                type: 'success',
                title: 'Elemento creado',
                message: 'Se ha creado un nuevo elemento'
              });
              
              // Refrescar estilos después de crear una feature
              setTimeout(async () => {
                if (mapService && typeof mapService.refreshDrawLayerStyles === 'function') {
                  await mapService.refreshDrawLayerStyles();
                  logger.log('DRAWING_PANEL', '🎨 DrawingPanel: Estilos refrescados después de crear feature');
                }
              }, 100);
            });

            drawingService.onFeatureDeleted(() => {
              addNotification({
                type: 'info',
                title: 'Elemento eliminado',
                message: 'Se ha eliminado el elemento seleccionado'
              });
            });

            drawingService.onFeatureSelected(() => {
              addNotification({
                type: 'info',
                title: 'Elemento seleccionado',
                message: 'Elemento seleccionado'
              });
            });

            // Si el panel está abierto cuando termina la init, iniciar modo de dibujo ahora
            if (isOpen) {
              logger.log('DRAWING_PANEL', '🎨 DrawingPanel: Iniciando modo de dibujo tras init');
              drawingService.startDrawMode();
            }
          }
        } catch (e) {
          if (mounted) console.error('Error inicializando DrawingService:', e);
        }
      }
    })();
    return () => { mounted = false; };
  }, [drawingService, setMode, addNotification, mapService, selectedStoreyId, isOpen]);

  // Actualizar planta seleccionada
  const handleStoreyChange = (storeyId: string) => {
    try {
              logger.log('DRAWING_PANEL', '🔄 DrawingPanel: Cambiando planta a:', storeyId);
      setSelectedStoreyId(storeyId);
      
      const gui = mapService?.getGUI();
      if (gui) {
        gui.selectStoreyById(storeyId);
        logger.log('DRAWING_PANEL', '✅ DrawingPanel: Planta cambiada en el mapa');
        
        // Actualizar el estado de visibilidad de la nueva planta seleccionada
        setTimeout(() => {
          const activeLayer = mapService?.getActiveDrawLayer();
          if (activeLayer) {
            setIsLayerVisible(activeLayer.getVisible());
            logger.log('DRAWING_PANEL', '✅ DrawingPanel: Visibilidad de capa actualizada');
          }
          
          // Evitar iniciar modos durante el cambio de planta para no forzar al servicio
        }, 100); // Pequeño delay para asegurar que el cambio de planta se haya procesado
      } else {
        console.warn('⚠️ DrawingPanel: GUI no disponible para cambiar planta');
      }
    } catch (error) {
      console.error('❌ DrawingPanel: Error al cambiar planta:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'No se pudo cambiar la planta'
      });
    }
  };

  // Actualizar categoría seleccionada
  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
            logger.log('DRAWING_PANEL', '🎨 DrawingPanel: Categoría cambiada a:', category);
    
    // Actualizar referencia global al store
    (window as any).__categoriesStore = useCategoriesStore.getState();
    
    // Recrear el servicio con la nueva categoría por defecto
    if (drawingService) {
      drawingService.destroy();
    }
    
    // Refrescar estilos después de cambiar categoría
    setTimeout(async () => {
      if (mapService && typeof mapService.refreshDrawLayerStyles === 'function') {
        await mapService.refreshDrawLayerStyles();
        logger.log('DRAWING_PANEL', '🎨 DrawingPanel: Estilos refrescados después de cambiar categoría');
      }
    }, 50);
  };

  const handleModeChange = (mode: DrawingMode) => {
    if (!drawingService) {
      addNotification({ type: 'error', title: 'Error', message: 'Servicio de dibujo no disponible' });
      return;
    }

    try {
      switch (mode) {
        case DrawingMode.DRAW:
          drawingService.startDrawMode();
          setDrawing(true);
          break;
        case DrawingMode.EDIT:
          drawingService.startEditMode();
          setDrawing(false);
          break;
        case DrawingMode.DELETE:
          drawingService.startDeleteMode();
          setDrawing(false);
          break;
        case DrawingMode.CONSULT:
          drawingService.startConsultMode();
          setDrawing(false);
          break;
        default:
          console.warn('Modo no implementado:', mode);
      }

      setMode(mode);
      addNotification({
        type: 'info',
        title: 'Modo cambiado',
        message: `Modo de dibujo: ${mode}`
      });
    } catch (error) {
      console.error('Error cambiando modo:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'No se pudo cambiar el modo de dibujo'
      });
    }
  };

  // ====== Capas auxiliares ======
  const [auxName, setAuxName] = useState('Capa auxiliar');
  const [assignAllStoreys, setAssignAllStoreys] = useState(true);
  const [assignedStoreys, setAssignedStoreys] = useState<string[]>([]);
  const [auxLayers, setAuxLayers] = useState<any[]>([]);
  const [editingAuxId, setEditingAuxId] = useState<string | null>(null);
  const [editingAssignAll, setEditingAssignAll] = useState<boolean>(true);
  const [editingAssignedStoreys, setEditingAssignedStoreys] = useState<string[]>([]);

  const refreshAuxLayers = () => {
    if (!mapService) return;
    try {
      setAuxLayers(mapService.listAuxLayers());
    } catch (_) {}
  };

  useEffect(() => {
    refreshAuxLayers();
  }, [mapService, isOpen]);

  const handleCreateAuxLayer = () => {
    if (!mapService) return;
    const assignment = assignAllStoreys ? 'all' : assignedStoreys;
    const api: any = mapService as any;
    if (typeof api.addEmptyAuxLayer !== 'function') {
      addNotification({ type: 'error', title: 'Capas auxiliares', message: 'API no disponible. Recarga la app.' });
      return;
    }
    const aux = api.addEmptyAuxLayer(auxName.trim() || 'Capa auxiliar', assignment as any);
    setAuxName('Capa auxiliar');
    setAssignedStoreys([]);
    setAssignAllStoreys(true);
    refreshAuxLayers();
    addNotification({ type: 'success', title: 'Capa auxiliar', message: `Creada: ${aux.name}` });
  };

  const handleUpdateAuxAssignment = (auxId: string) => {
    if (!mapService) return;
    const current = auxLayers.find(a => a.id === auxId);
    setEditingAuxId(auxId);
    if (Array.isArray(current?.assignedStoreyIds)) {
      setEditingAssignAll(false);
      setEditingAssignedStoreys(current!.assignedStoreyIds);
    } else {
      setEditingAssignAll(true);
      setEditingAssignedStoreys([]);
    }
  };

  const handleDeleteAux = (auxId: string) => {
    if (!mapService) return;
    mapService.removeAuxLayer(auxId);
    refreshAuxLayers();
  };

  const handleClearAux = () => {
    if (!mapService) return;
    if (confirm('¿Eliminar todas las capas auxiliares?')) {
      mapService.clearAuxLayers();
      refreshAuxLayers();
    }
  };

  const handleSaveEditingAux = () => {
    if (!mapService || !editingAuxId) return;
    const assignment = editingAssignAll ? 'all' : editingAssignedStoreys;
    mapService.setAuxLayerAssignment(editingAuxId, assignment as any);
    setEditingAuxId(null);
    refreshAuxLayers();
  };

  const handleCancelEditingAux = () => {
    setEditingAuxId(null);
  };

  const handleToggleLayerVisibility = () => {
    if (!drawingService) {
      addNotification({ type: 'error', title: 'Error', message: 'Servicio de dibujo no disponible' });
      return;
    }

    try {
      drawingService.toggleActiveLayerVisibility();
      
      // Actualizar el estado local después de cambiar la visibilidad
      const activeLayer = mapService?.getActiveDrawLayer();
      if (activeLayer) {
        setIsLayerVisible(activeLayer.getVisible());
      }
      
      addNotification({
        type: 'info',
        title: 'Visibilidad',
        message: 'Visibilidad de la capa de dibujo cambiada'
      });
    } catch (error) {
      console.error('Error cambiando visibilidad:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'No se pudo cambiar la visibilidad'
      });
    }
  };



  // Función para borrar todos los dibujos de todas las plantas
  const handleClearAllDrawings = () => {
    if (!mapService) {
      addNotification({ type: 'error', title: 'Error', message: 'Servicio de mapa no disponible' });
      return;
    }

    // Crear modal de confirmación
    const modal = document.createElement('div');
    modal.className = 'v2-copy-modal-overlay';
    modal.innerHTML = `
      <div class="v2-copy-modal">
        <h3>⚠️ Confirmar borrado</h3>
        <p>¿Estás seguro de que quieres borrar <strong>TODOS</strong> los dibujos de <strong>TODAS</strong> las plantas?</p>
        <p><strong>Esta acción no se puede deshacer.</strong></p>
        <div class="v2-copy-modal-buttons">
          <button id="confirmClear" class="v2-copy-modal-button v2-copy-modal-button-danger">Sí, borrar todo</button>
          <button id="cancelClear" class="v2-copy-modal-button v2-copy-modal-button-secondary">Cancelar</button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    // Función para limpiar el modal
    const cleanup = () => {
      document.body.removeChild(modal);
    };

    // Eventos de los botones
    document.getElementById('confirmClear')?.addEventListener('click', () => {
      try {
        // Obtener todas las plantas disponibles
        const gui = mapService.getGUI();
        const availableStoreys = gui?.getStoreyDropdownOptions() || [];
        
        let totalDeleted = 0;
        
        // Borrar dibujos de todas las plantas
        availableStoreys.forEach((storey: any) => {
          const layer = mapService.getOrCreateDrawLayer(storey.id);
          if (layer) {
            const features = layer.getSource()?.getFeatures() || [];
            layer.getSource()?.clear();
            totalDeleted += features.length;
          }
        });

        // Limpiar el store unificado de elementos
        const { clearAllElements, syncToMap } = useUnifiedElementsStore.getState();
        clearAllElements();
        
        // Sincronizar con el mapa para asegurar que se limpien todas las capas
        syncToMap();
        
        // Limpiar etiquetas del mapa
        mapService.hideElementLabel();

        cleanup();
        
        addNotification({
          type: 'success',
          title: 'Dibujos borrados',
          message: `Se han borrado ${totalDeleted} elementos de todas las plantas`
        });

        // Actualizar el estado de selección
        setDrawing(false);
        setMode(DrawingMode.DRAW);
        
      } catch (error) {
        console.error('Error borrando dibujos:', error);
        cleanup();
        addNotification({
          type: 'error',
          title: 'Error',
          message: 'No se pudieron borrar todos los dibujos'
        });
      }
    });

    document.getElementById('cancelClear')?.addEventListener('click', cleanup);
  };

  // Función para copiar elementos a otra planta
  const handleCopyToFloor = () => {
    if (!mapService || !drawingService) {
      addNotification({ type: 'error', title: 'Error', message: 'Servicios no disponibles' });
      return;
    }

    const gui = mapService.getGUI();
    if (!gui) {
      addNotification({ type: 'error', title: 'Error', message: 'GUI no disponible' });
      return;
    }

    // Obtener la planta activa y sus elementos
    const activeLayer = mapService.getActiveDrawLayer();
    if (!activeLayer) {
      addNotification({ type: 'warning', title: 'Advertencia', message: 'No hay planta seleccionada para copiar' });
      return;
    }

    const activeFeatures = activeLayer.getSource()?.getFeatures() || [];
    if (activeFeatures.length === 0) {
      addNotification({ type: 'warning', title: 'Advertencia', message: 'No hay elementos para copiar en la planta actual' });
      return;
    }

    // Crear modal para seleccionar planta destino
    const modal = document.createElement('div');
    modal.className = 'v2-copy-modal-overlay';
    modal.innerHTML = `
      <div class="v2-copy-modal">
        <h3>Seleccionar planta destino</h3>
                 <select id="targetStoreySelect" class="v2-copy-modal-select">
           ${(() => {
             const gui = mapService?.getGUI();
             const availableStoreys = gui?.getStoreyDropdownOptions() || [];
             return availableStoreys
               .filter((opt: any) => opt.id !== selectedStoreyId)
               .map((opt: any) => `<option value="${opt.id}">${opt.name}</option>`)
               .join('');
           })()}
         </select>
        <div class="v2-copy-modal-buttons">
          <button id="confirmCopy" class="v2-copy-modal-button v2-copy-modal-button-primary">Confirmar</button>
          <button id="cancelCopy" class="v2-copy-modal-button v2-copy-modal-button-secondary">Cancelar</button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    // Función para limpiar el modal
    const cleanup = () => {
      document.body.removeChild(modal);
    };

    // Eventos de los botones
    document.getElementById('confirmCopy')?.addEventListener('click', () => {
      const targetStoreyId = (document.getElementById('targetStoreySelect') as HTMLSelectElement)?.value;
      if (targetStoreyId) {
        const targetLayer = mapService.getOrCreateDrawLayer(targetStoreyId);
        if (targetLayer) {
          // Verificar si la planta destino ya tiene elementos
          const existingFeatures = targetLayer.getSource()?.getFeatures() || [];
          if (existingFeatures.length > 0) {
            // Crear modal de confirmación
            const confirmModal = document.createElement('div');
            confirmModal.className = 'v2-copy-modal-overlay';
            confirmModal.innerHTML = `
              <div class="v2-copy-modal">
                <h3>La planta destino ya tiene elementos</h3>
                <p>¿Qué deseas hacer?</p>
                <div class="v2-copy-modal-buttons">
                  <button id="replace" class="v2-copy-modal-button v2-copy-modal-button-primary">Reemplazar todos</button>
                  <button id="combine" class="v2-copy-modal-button v2-copy-modal-button-secondary">Combinar</button>
                  <button id="cancel" class="v2-copy-modal-button v2-copy-modal-button-secondary">Cancelar</button>
                </div>
              </div>
            `;

            document.body.appendChild(confirmModal);

            // Eventos de los botones de confirmación
            document.getElementById('replace')?.addEventListener('click', () => {
              targetLayer.getSource()?.clear();
              activeFeatures.forEach(feature => {
                const featureClone = feature.clone();
                targetLayer.getSource()?.addFeature(featureClone);
              });
              addNotification({
                type: 'success',
                title: 'Elementos copiados',
                message: `Elementos copiados a la planta ${targetStoreyId} (reemplazando elementos existentes)`
              });
              document.body.removeChild(confirmModal);
              cleanup();
            });

            document.getElementById('combine')?.addEventListener('click', () => {
              activeFeatures.forEach(feature => {
                const featureClone = feature.clone();
                targetLayer.getSource()?.addFeature(featureClone);
              });
              addNotification({
                type: 'success',
                title: 'Elementos copiados',
                message: `Elementos copiados a la planta ${targetStoreyId} (combinando con elementos existentes)`
              });
              document.body.removeChild(confirmModal);
              cleanup();
            });

            document.getElementById('cancel')?.addEventListener('click', () => {
              document.body.removeChild(confirmModal);
              cleanup();
            });
          } else {
            // Si no hay elementos, copiar directamente
            activeFeatures.forEach(feature => {
              const featureClone = feature.clone();
              targetLayer.getSource()?.addFeature(featureClone);
            });
            addNotification({
              type: 'success',
              title: 'Elementos copiados',
              message: `Elementos copiados a la planta ${targetStoreyId}`
            });
            cleanup();
          }
        }
      }
    });

    document.getElementById('cancelCopy')?.addEventListener('click', cleanup);
  };

  // Función para guardar dibujos usando el store unificado
  const handleSaveDrawings = () => {
    if (!mapService) {
      addNotification({ type: 'error', title: 'Error', message: 'Servicio de mapa no disponible' });
      return;
    }

    try {
      // Usar el store unificado para exportar
      const unifiedStore = useUnifiedElementsStore.getState();
      const drawingsData = unifiedStore.exportToGeoJSON();
      
      // Contar total de dibujos
      const totalDrawings = Object.values(drawingsData).reduce((total, drawings) => total + drawings.length, 0);
              logger.log('DRAWING_PANEL', `📊 Guardando ${totalDrawings} dibujos de ${Object.keys(drawingsData).length} plantas`);
      
      // Crear el archivo JSON
      const dataStr = JSON.stringify(drawingsData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      
      // Obtener el nombre del archivo IFC cargado
      const gui = mapService.getGUI();
      let fileName = 'dibujos';
      if (gui && gui.getIfcFileName) {
        const ifcFileName = gui.getIfcFileName();
        if (ifcFileName && ifcFileName.trim() !== '') {
          fileName = ifcFileName.replace(/[^a-zA-Z0-9_-]/g, '_');
        }
      }
      
      // Crear enlace de descarga
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileName}_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      addNotification({
        type: 'success',
        title: 'Dibujos guardados',
        message: `Se han exportado ${totalDrawings} dibujos de ${Object.keys(drawingsData).length} plantas correctamente`
      });
      
    } catch (error) {
      console.error('Error al guardar dibujos:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'No se pudieron guardar los dibujos'
      });
    }
  };

  // Función para cargar dibujos
  const handleLoadDrawings = () => {
    fileInputRef.current?.click();
  };

    // Función para procesar el archivo de dibujos con compatibilidad mejorada
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const drawingsData = JSON.parse(e.target?.result as string);
        logger.log('DRAWING_PANEL', '📁 Datos de dibujos cargados:', drawingsData);
        
        if (!mapService) {
          addNotification({ type: 'error', title: 'Error', message: 'Servicio de mapa no disponible' });
          return;
        }
        
        // Validar estructura del archivo
        if (typeof drawingsData !== 'object' || drawingsData === null) {
          throw new Error('Formato de archivo inválido: debe ser un objeto JSON');
        }
        
        // Asegurar que el MapService esté configurado en el store unificado
        const unifiedStore = useUnifiedElementsStore.getState();
        if (!unifiedStore.mapService) {
          logger.log('DRAWING_PANEL', '🔧 DrawingPanel: Configurando MapService en store unificado');
          unifiedStore.setMapService(mapService);
        }
        
        // Limpiar todos los elementos usando el store unificado
        unifiedStore.clearAllElements();
        
        // Importar los datos usando el store unificado
        unifiedStore.importFromGeoJSON(drawingsData);
        
        // Obtener el total de elementos importados
        const totalImported = unifiedStore.getTotalElements();
        logger.log('DRAWING_PANEL', `✅ Dibujos importados correctamente. Total de elementos: ${totalImported}`);
        
        // Forzar sincronización con el mapa después de un pequeño delay
        setTimeout(() => {
          unifiedStore.syncToMap();
          logger.log('DRAWING_PANEL', '🔄 DrawingPanel: Sincronización forzada completada');
        }, 500);
        
        addNotification({
          type: 'success',
          title: 'Dibujos importados',
          message: `Se han importado ${totalImported} elementos correctamente`
        });
        
        // Activar automáticamente el modo de dibujo después de importar
        logger.log('DRAWING_PANEL', '🎨 DrawingPanel: Activando modo de dibujo después de importar');
        setMode(DrawingMode.DRAW);
        setDrawing(true);
        
      } catch (error) {
        console.error('❌ Error al importar dibujos:', error);
        addNotification({
          type: 'error',
          title: 'Error',
          message: `Error al importar el archivo: ${error instanceof Error ? error.message : 'Formato inválido'}`
        });
      }
    };
    
    reader.readAsText(file);
    
    // Limpiar el input para permitir cargar el mismo archivo de nuevo
    event.target.value = '';
  };

  if (!isOpen) return null;

  return (
    <div className="v2-drawing-panel">
      <div className="v2-drawing-panel-header">
        <h3 className="v2-drawing-panel-title">Herramientas de Dibujo</h3>
        <button
          className="v2-drawing-panel-close"
          onClick={onClose}
        >
          ×
        </button>
      </div>

      <div className="v2-drawing-panel-content">
        <div className="v2-drawing-panel-section">
          <h4>Configuración</h4>

          <div className="v2-drawing-panel-config">
                                    <div className="v2-drawing-panel-config-item">
                          <label className="v2-drawing-panel-label">Planta:</label>
                          <select
                            className="v2-drawing-panel-select"
                            value={selectedStoreyId}
                            onChange={(e) => {
                              logger.log('DRAWING_PANEL', '🔄 DrawingPanel: Cambiando planta a:', e.target.value);
                              handleStoreyChange(e.target.value);
                            }}
                          >
                            {(() => {
                              const gui = mapService?.getGUI();
                              const availableStoreys = gui?.getStoreyDropdownOptions() || [];
                              
                              if (availableStoreys.length === 0) {
                                return <option value="">Cargar IFC primero</option>;
                              } else {
                                return availableStoreys.map(option => (
                                  <option key={option.id} value={option.id}>
                                    {option.name}
                                  </option>
                                ));
                              }
                            })()}
                          </select>
                        </div>

            <div className="v2-drawing-panel-config-item">
              <label className="v2-drawing-panel-label">Categoría:</label>
              <select
                className="v2-drawing-panel-select"
                value={selectedCategory}
                onChange={(e) => handleCategoryChange(e.target.value)}
              >
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="v2-drawing-panel-section">
          <h4>Modos de Dibujo</h4>

          <div className="v2-drawing-panel-tools">
            <button
              className={`v2-drawing-panel-tool ${currentMode === DrawingMode.DRAW ? 'active' : ''}`}
              onClick={() => handleModeChange(DrawingMode.DRAW)}
              title="Dibujar polígonos"
            >
              <svg className="v2-drawing-icon" viewBox="0 0 24 24">
                <path d="M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87L20.71,7.04Z"/>
              </svg>
              Dibujar
            </button>

            <button
              className={`v2-drawing-panel-tool ${currentMode === DrawingMode.EDIT ? 'active' : ''}`}
              onClick={() => handleModeChange(DrawingMode.EDIT)}
              title="Editar elementos"
            >
              <svg className="v2-drawing-icon" viewBox="0 0 24 24">
                <path d="M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87L20.71,7.04M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z"/>
              </svg>
              Editar
            </button>

            <button
              className={`v2-drawing-panel-tool ${currentMode === DrawingMode.DELETE ? 'active' : ''}`}
              onClick={() => handleModeChange(DrawingMode.DELETE)}
              title="Eliminar elementos"
            >
              <svg className="v2-drawing-icon" viewBox="0 0 24 24">
                <path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z"/>
              </svg>
              Eliminar
            </button>

            <button
              className={`v2-drawing-panel-tool ${currentMode === DrawingMode.CONSULT ? 'active' : ''}`}
              onClick={() => handleModeChange(DrawingMode.CONSULT)}
              title="Consultar información"
            >
              <svg className="v2-drawing-icon" viewBox="0 0 24 24">
                <path d="M11,9H13V7H11M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M11,17H13V11H11V17Z"/>
              </svg>
              Consultar
            </button>
          </div>
        </div>

        <div className="v2-drawing-panel-section">
          <h4>Acciones</h4>

          <div className="v2-drawing-panel-actions">
            <button
              className="v2-drawing-panel-button"
              onClick={handleToggleLayerVisibility}
            >
                             {isLayerVisible ? 'Ocultar capa' : 'Mostrar capa'}
            </button>

            <button
              className="v2-drawing-panel-button"
              onClick={handleCopyToFloor}
            >
              Copiar a otra planta
            </button>

            <button
              className="v2-drawing-panel-button"
              onClick={handleSaveDrawings}
            >
              Guardar dibujos
            </button>

                                     <button
              className="v2-drawing-panel-button"
              onClick={handleLoadDrawings}
            >
              Importar dibujos
            </button>

            <button
              className="v2-drawing-panel-button v2-drawing-panel-button-danger"
              onClick={handleClearAllDrawings}
            >
              🗑️ Borrar todos los dibujos
            </button>

             
          </div>
        </div>

        <div className="v2-drawing-panel-section">
          <h4>Capas auxiliares</h4>
          <div className="v2-drawing-panel-config">
            <div className="v2-drawing-panel-config-item" style={{ gridColumn: '1 / -1' }}>
              <label className="v2-drawing-panel-label">Nombre:</label>
              <input className="v2-drawing-panel-input" type="text" value={auxName} onChange={(e) => setAuxName(e.target.value)} />
            </div>
            <div className="v2-drawing-panel-config-item" style={{ gridColumn: '1 / -1' }}>
              <label className="v2-drawing-panel-label">
                <input type="checkbox" checked={assignAllStoreys} onChange={(e) => setAssignAllStoreys(e.target.checked)} />
                &nbsp;Asignar a todas las plantas
              </label>
            </div>
            {!assignAllStoreys && (
              <div className="v2-drawing-panel-config-item" style={{ gridColumn: '1 / -1' }}>
                <label className="v2-drawing-panel-label">Plantas:</label>
                <select
                  className="v2-drawing-panel-select"
                  multiple
                  size={6}
                  value={assignedStoreys}
                  onChange={(e) => {
                    const options = Array.from((e.target as HTMLSelectElement).selectedOptions).map(o => o.value);
                    setAssignedStoreys(options);
                  }}
                >
                  {(() => {
                    const gui = mapService?.getGUI();
                    const availableStoreys = gui?.getStoreyDropdownOptions() || [];
                    return availableStoreys.map((opt: any) => (
                      <option key={opt.id} value={opt.id}>{opt.name}</option>
                    ));
                  })()}
                </select>
              </div>
            )}
            <div className="v2-drawing-panel-actions" style={{ gridColumn: '1 / -1' }}>
              <button className="v2-drawing-panel-button" onClick={handleCreateAuxLayer}>Crear capa auxiliar</button>
              <button className="v2-drawing-panel-button v2-drawing-panel-button-danger" onClick={handleClearAux}>Eliminar todas</button>
            </div>
          </div>

          {auxLayers.length > 0 && (
            <div style={{ marginTop: 8 }}>
              {auxLayers.map((a) => (
                <div key={a.id} style={{ padding: '8px 0', borderTop: '1px dashed #eee' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500 }}>{a.name}</div>
                      <div style={{ fontSize: 12, opacity: 0.7 }}>Asignación: {Array.isArray(a.assignedStoreyIds) ? a.assignedStoreyIds.join(',') : 'todas'}</div>
                    </div>
                    <button title="Editar asignación" className="v2-drawing-panel-icon" onClick={() => handleUpdateAuxAssignment(a.id)}>
                      ✏️
                    </button>
                    <button title="Eliminar" className="v2-drawing-panel-icon" onClick={() => handleDeleteAux(a.id)}>
                      🗑️
                    </button>
                  </div>

                  {editingAuxId === a.id && (
                    <div style={{ marginTop: 8, padding: 8, background: 'rgba(0,0,0,0.03)', borderRadius: 6 }}>
                      <label className="v2-drawing-panel-label" style={{ display: 'block', marginBottom: 6 }}>
                        <input type="checkbox" checked={editingAssignAll} onChange={(e) => setEditingAssignAll(e.target.checked)} />
                        &nbsp;Asignar a todas las plantas
                      </label>
                      {!editingAssignAll && (
                        <select
                          className="v2-drawing-panel-select"
                          multiple
                          size={6}
                          value={editingAssignedStoreys}
                          onChange={(e) => {
                            const options = Array.from((e.target as HTMLSelectElement).selectedOptions).map(o => o.value);
                            setEditingAssignedStoreys(options);
                          }}
                          style={{ width: '100%' }}
                        >
                          {(() => {
                            const gui = mapService?.getGUI();
                            const availableStoreys = gui?.getStoreyDropdownOptions() || [];
                            return availableStoreys.map((opt: any) => (
                              <option key={opt.id} value={opt.id}>{opt.name}</option>
                            ));
                          })()}
                        </select>
                      )}
                      <div style={{ marginTop: 8, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button className="v2-drawing-panel-button" onClick={handleSaveEditingAux}>Guardar</button>
                        <button className="v2-drawing-panel-button v2-drawing-panel-button-secondary" onClick={handleCancelEditingAux}>Cancelar</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        

        <div className="v2-drawing-panel-section">
          <h4>Estado</h4>

          <div className="v2-drawing-panel-status">
            <div className="v2-drawing-panel-status-item">
              <span className="v2-drawing-panel-label">Modo actual:</span>
              <span className="v2-drawing-panel-value">{currentMode}</span>
            </div>

            <div className="v2-drawing-panel-status-item">
              <span className="v2-drawing-panel-label">Dibujando:</span>
              <span className="v2-drawing-panel-value">{isDrawing ? 'Sí' : 'No'}</span>
            </div>

            <div className="v2-drawing-panel-status-item">
              <span className="v2-drawing-panel-label">Elementos seleccionados:</span>
              <span className="v2-drawing-panel-value">{selectedFeatures.length}</span>
            </div>
          </div>
        </div>

        <div className="v2-drawing-panel-info">
          <strong>Información:</strong>
          <ul>
            <li>Carga un archivo IFC primero para habilitar las plantas</li>
            <li>Selecciona una planta y categoría antes de dibujar</li>
            <li>Haz clic derecho para cancelar el dibujo actual</li>
            <li>Los elementos se crean en la planta activa</li>
            <li>Usa "Consultar" para ver información de elementos</li>
            <li>Usa "Copiar a otra planta" para duplicar elementos</li>
            <li>Usa "Guardar dibujos" para exportar los elementos</li>
            <li>Usa "Importar dibujos" para cargar elementos guardados</li>
          </ul>
        </div>

        {/* Input oculto para importar archivos */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".json,.geojson"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </div>
    </div>
  );
};

export default DrawingPanel;
